import { useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import {
  LABOR_OPEN_SESSION_QUERY_KEY,
} from "@/components/LaborActiveSessionProvider";
import { fetchLaborOpenSession, type LaborActiveSessionPayload } from "@/lib/labor/laborApi";
import { isLaborShellLockdownBypassed } from "@/lib/labor/laborEnv";
import { readLaborDevShellLock, setLaborDevShellLock } from "@/lib/labor/laborDevShellLock";
import { decideLaborHandoff, type LaborHandoffDecision } from "@/lib/labor/laborHandoff";
import { clearLaborDepartureSnapshot } from "@/lib/labor/laborDepartureSnapshot";
import { laborDomainFromSessionPage } from "@/lib/labor/laborDomainFromPage";
import { hubLaborExpectedPageFromPathname, isWorkingSubpageKey } from "@/lib/labor/laborPageKey";
import { runWhileLaborOwnerDeparting, setLaborOwnerDeparting } from "@/lib/labor/laborOwnerDeparture";
import { resolvePresenceForNavigationSync } from "@/lib/labor/laborOpenSessionCache";
import type { LaborPresence } from "@/lib/labor/laborPresence";
import { resolveLaborPresence } from "@/lib/labor/laborPresence";
import {
  backgroundEndOpenLaborServerSessionIfAny,
  flushLaborServerClockOut,
  optimisticallyClearOpenLaborSession,
  prepareLaborNavigationClockOut,
  retryPendingLaborClockOut,
} from "@/lib/labor/laborReleaseHubPresence";
import { getLaborTabId } from "@/lib/labor/laborTabId";
import { openSessionFromLaborPayload } from "@/lib/labor/parseLaborSession";

/**
 * SPA navigation in the tab that owns labor presence:
 * - `handoff`   — working-to-working: end the server session or rewrite the local dev shell lock
 *                 to the next working key so the destination route can adopt or restart cleanly.
 * - `clock_out` — working-to-non-working: silently end the server session or clear the local dev
 *                 shell lock as part of leaving the page. The owner gets to leave; other tabs and
 *                 other computers no longer see presence and `LaborLockdownGate` releases their
 *                 lockdown on the next storage event / refetch.
 * - `noop`      — every other case (presence belongs to a different tab, presence is external,
 *                 the previous URL was not the page that owns presence, or there is no presence).
 *                 Cross-tab / cross-computer lockdown is owned entirely by `LaborLockdownGate`.
 *
 * Handoff runs on route commit. The hub uses `BrowserRouter` (not a data router), so React Router
 * `useBlocker` is unavailable here.
 */

/** Rare fallback when the open-session cache was never loaded in this tab. */
async function resolvePresenceForNavigationAsync(queryClient: QueryClient): Promise<LaborPresence | null> {
  const payload = await queryClient.fetchQuery<LaborActiveSessionPayload>({
    queryKey: [...LABOR_OPEN_SESSION_QUERY_KEY],
    queryFn: () => fetchLaborOpenSession(),
  });
  return resolveLaborPresence({
    openSession: openSessionFromLaborPayload(payload),
    devShell: readLaborDevShellLock(),
    activeOnOtherPage: payload.active_on_other_page,
    activeOnOtherSource: payload.active_on_other_source,
  });
}

function isLeavingWorkingForNonWorking(input: {
  previousPathname: string;
  previousSearch: string;
  nextPathname: string;
  nextSearch: string;
}): boolean {
  const previousKey = hubLaborExpectedPageFromPathname(input.previousPathname, input.previousSearch);
  const nextKey = hubLaborExpectedPageFromPathname(input.nextPathname, input.nextSearch);
  return isWorkingSubpageKey(previousKey) && !isWorkingSubpageKey(nextKey);
}

function clockOutWhenLeavingWorkingWithoutPresence(input: {
  queryClient: QueryClient;
  prev: { pathname: string; search: string };
  nextPath: string;
  nextSearch: string;
}): void {
  if (
    !isLeavingWorkingForNonWorking({
      previousPathname: input.prev.pathname,
      previousSearch: input.prev.search,
      nextPathname: input.nextPath,
      nextSearch: input.nextSearch,
    })
  ) {
    return;
  }
  runWhileLaborOwnerDeparting(() => {
    optimisticallyClearOpenLaborSession(input.queryClient);
    clearLaborDepartureSnapshot();
  });
  backgroundEndOpenLaborServerSessionIfAny(input.queryClient);
}

function runLaborNavigationBackgroundEnd(input: {
  queryClient: QueryClient;
  sessionId: number;
  domain: ReturnType<typeof laborDomainFromSessionPage>;
}): void {
  setLaborOwnerDeparting(true);
  void (async () => {
    try {
      await flushLaborServerClockOut(input.queryClient, {
        sessionId: input.sessionId,
        domain: input.domain,
        keepalive: true,
      });
    } finally {
      setLaborOwnerDeparting(false);
    }
  })();
}

function executeLaborNavigationHandoff(input: {
  decision: LaborHandoffDecision;
  presence: LaborPresence;
  nextPathname: string;
  nextSearch: string;
  queryClient: QueryClient;
}): void {
  const { decision, presence, nextPathname, nextSearch, queryClient } = input;
  if (decision === "noop") {
    return;
  }

  const serverSessionId = presence.serverSessionId;
  const isServer = presence.source === "server" && serverSessionId !== null && serverSessionId > 0;
  const isLocal = presence.source === "local";
  const domain = laborDomainFromSessionPage(presence.page);

  if (decision === "handoff") {
    const nextKey = hubLaborExpectedPageFromPathname(nextPathname, nextSearch);
    if (isLocal) {
      runWhileLaborOwnerDeparting(() => {
        optimisticallyClearOpenLaborSession(queryClient);
        setLaborDevShellLock({ page: nextKey });
      });
      return;
    }
    if (isServer && serverSessionId !== null) {
      // Mark departing before the optimistic cache clear so still-mounted labor
      // hooks do not treat this tab's leave as an unexpected remote clock-out.
      setLaborOwnerDeparting(true);
      prepareLaborNavigationClockOut(queryClient, { sessionId: serverSessionId, domain });
      runLaborNavigationBackgroundEnd({ queryClient, sessionId: serverSessionId, domain });
    }
    return;
  }

  if (isServer && serverSessionId !== null) {
    setLaborOwnerDeparting(true);
    prepareLaborNavigationClockOut(queryClient, { sessionId: serverSessionId, domain });
    runLaborNavigationBackgroundEnd({ queryClient, sessionId: serverSessionId, domain });
    return;
  }

  if (isLocal) {
    runWhileLaborOwnerDeparting(() => {
      optimisticallyClearOpenLaborSession(queryClient);
      clearLaborDepartureSnapshot();
    });
  }
}

function handleLaborNavigation(input: {
  queryClient: QueryClient;
  tabId: string;
  prev: { pathname: string; search: string };
  nextPath: string;
  nextSearch: string;
  presence: LaborPresence;
}): void {
  const decision = decideLaborHandoff({
    openSessionPage: input.presence.page,
    openSessionTabId: input.presence.tabId,
    currentTabId: input.tabId,
    previousPathname: input.prev.pathname,
    previousSearch: input.prev.search,
    nextPathname: input.nextPath,
    nextSearch: input.nextSearch,
  });
  if (decision === "noop") {
    return;
  }

  executeLaborNavigationHandoff({
    decision,
    presence: input.presence,
    nextPathname: input.nextPath,
    nextSearch: input.nextSearch,
    queryClient: input.queryClient,
  });
}

export function LaborSpaNavigationWatch(): null {
  const location = useLocation();
  const queryClient = useQueryClient();
  const prevLocationRef = useRef<{ pathname: string; search: string } | null>(null);

  useLayoutEffect(() => {
    void retryPendingLaborClockOut(queryClient);
  }, [queryClient]);

  useLayoutEffect(() => {
    const nextPath = location.pathname;
    const nextSearch = location.search;
    const prev = prevLocationRef.current;
    prevLocationRef.current = { pathname: nextPath, search: nextSearch };
    if (prev === null) {
      return;
    }
    if (prev.pathname === nextPath && prev.search === nextSearch) {
      return;
    }

    if (isLaborShellLockdownBypassed()) {
      return;
    }

    void retryPendingLaborClockOut(queryClient);

    const tabId = getLaborTabId();
    const sync = resolvePresenceForNavigationSync(queryClient);
    if (sync.cacheLoaded) {
      if (sync.presence === null) {
        clockOutWhenLeavingWorkingWithoutPresence({
          queryClient,
          prev,
          nextPath,
          nextSearch,
        });
        return;
      }
      handleLaborNavigation({
        queryClient,
        tabId,
        prev,
        nextPath,
        nextSearch,
        presence: sync.presence,
      });
      return;
    }

    void (async () => {
      const presence = await resolvePresenceForNavigationAsync(queryClient);
      if (presence === null) {
        clockOutWhenLeavingWorkingWithoutPresence({
          queryClient,
          prev,
          nextPath,
          nextSearch,
        });
        return;
      }
      handleLaborNavigation({
        queryClient,
        tabId,
        prev,
        nextPath,
        nextSearch,
        presence,
      });
    })();
  }, [location.pathname, location.search, queryClient]);

  return null;
}
